import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Trophy,
  Target,
  Brain,
  BookOpen,
  Zap,
  Lock,
  CheckCircle2,
  ChevronRight,
  Loader2,
  X,
  MessageSquare,
  Clipboard,
  Star,
  TrendingUp,
  Award,
  Crown
} from 'lucide-react';
import api from '../services/api';

// The Beta 10 Questions Database
const ATHLETE_QUESTIONS = {
  week1: { // Quantitative Combine (Math)
    title: "The Quantitative Combine",
    subtitle: "Numbers Game",
    icon: Target,
    color: "orange",
    questions: [
      {
        id: "q1",
        type: "math",
        standard: "CCSS.MATH.7.G.B.4",
        competency: "Geometry & Spatial Awareness",
        question: "You are a Point Guard bringing the ball up the court. The half-court area is 47 feet long and 50 feet wide. If a defender's wing-span creates a 7-foot radius of \"denial\" around them, what is the remaining \"safe\" square footage of the half-court you can operate in? (Use 3.14 for π).",
        options: ["2,196.14 sq ft", "2,350 sq ft", "2,196.14 sq ft", "2,503.86 sq ft"],
        correctAnswer: 0,
        coachHint: "Scholar-Athlete, to find your operating space, we first need the total area of the half-court (Length×Width). Then, we have to subtract the 'Dead Zone' the defender is guarding. Since that zone is a circle, use the formula Area=πr². What's left is your lane to the basket."
      },
      {
        id: "q2",
        type: "math",
        standard: "CCSS.MATH.7.RP.A.3",
        competency: "Percentages & Efficiency",
        question: "A scout is watching your last 5 games. You took 80 shots and made 36. To reach the \"Elite Tier\" (55% shooting), how many consecutive shots do you need to make in your next game to reach that efficiency rating?",
        options: ["30 shots", "34 shots", "38 shots", "42 shots"],
        correctAnswer: 1,
        coachHint: "Scholar-Athlete, your efficiency is a ratio. Currently, you are at 36/80. We need to find how many 'Makes' (x) you need to add to both your makes and your total attempts to equal 0.55. Set it up like this: (36+x)/(80+x)=0.55."
      },
      {
        id: "q3",
        type: "math",
        standard: "CCSS.MATH.8.EE.C.7",
        competency: "Algebra & Budgeting",
        question: "You are managing an NIL collective with a $100,000 budget for 5 athletes. Athlete A takes 30%. Athlete B takes $15,000. If the remaining 3 athletes are paid equally (x), what is the contract value for each?",
        options: ["$15,000", "$16,667", "$18,333", "$20,000"],
        correctAnswer: 2,
        coachHint: "Scholar-Athlete, look at the salary cap. First, calculate 30% of the $100k for Athlete A and add the $15k for Athlete B. Subtract those from the total. Now, take that remaining balance and divide it by the 3 roster spots left to find x."
      }
    ]
  },
  week2: { // Tactical Literacy (Reading)
    title: "Tactical Literacy",
    subtitle: "The Language",
    icon: BookOpen,
    color: "blue",
    questions: [
      {
        id: "q4",
        type: "literacy",
        standard: "CCSS.ELA-LITERACY.L.8.4",
        competency: "Vocabulary in Context",
        question: "Your NIL contract states: \"The Athlete grants the Brand exclusive and irrevocable rights to use their likeness for the duration of the term.\" If a rival brand asks you to post a photo wearing their gear, are you legally allowed to do so?",
        options: ["Yes, as long as you tag both brands", "Yes, but only after the term ends", "No, exclusive means only one brand", "It depends on the compensation"],
        correctAnswer: 2,
        coachHint: "Scholar-Athlete, the key to this play is the word 'Exclusive.' In the recruiting world, if you sign an exclusive letter of intent, are you allowed to visit other schools? Apply that same logic to this brand contract."
      },
      {
        id: "q5",
        type: "literacy",
        standard: "CCSS.ELA-LITERACY.WHST.6-8.4",
        competency: "Tone & Branding",
        question: "After a tough loss, a reporter asks: \"Why did the team fall apart in the 4th quarter?\" Which response protects your Brand Identity without blaming teammates?",
        options: [
          "My teammates didn't hit their shots.",
          "The coaching staff made bad calls.",
          "Execution starts with me; we'll review the film and tighten up our rotations.",
          "We just had an off night, it happens."
        ],
        correctAnswer: 2,
        coachHint: "Scholar-Athlete, remember your 'Pillar of Accountability.' One of these answers sounds like a leader taking ownership, while the others sound like a liability to the locker room. Which one builds your legacy?"
      }
    ]
  },
  week3: { // Strategic Mind (Logic)
    title: "The Strategic Mind",
    subtitle: "Strategic Logic",
    icon: Brain,
    color: "purple",
    questions: [
      {
        id: "q6",
        type: "logic",
        standard: "Deductive Reasoning",
        competency: "Pattern Recognition",
        question: "In the last 3 possessions, the defense has shifted into a \"2-3 Zone\" every time you put two players in the corner. If you move two players to the corner on this play, what is the logical prediction for the defense?",
        options: [
          "Man-to-man defense",
          "Full court press",
          "2-3 Zone defense",
          "Box-and-one defense"
        ],
        correctAnswer: 2,
        coachHint: "Scholar-Athlete, this is about reading the defense. If the opponent has shown a consistent 'Reaction' to your 'Action' three times in a row, what should you expect them to do next? Don't get caught by surprise."
      },
      {
        id: "q7",
        type: "logic",
        standard: "Probability & Expected Value",
        competency: "Risk vs. Reward",
        question: "Down by 2 points with 5 seconds left. Your 3-point shooter is 30% from the arc. Your power forward is 65% from the paint. Which play has a higher \"Expected Value\" (EV) for a win in regulation?",
        options: [
          "3-point shot (EV = 0.90)",
          "2-point shot (EV = 1.30)",
          "Both have equal EV",
          "Timeout and draw up a new play"
        ],
        correctAnswer: 1,
        coachHint: "Scholar-Athlete, do the math on the 'Expected Value.' Calculate 0.30×3 points and compare it to 0.65×2 points. The higher number tells you which shot is mathematically more likely to result in the most points."
      }
    ]
  },
  week4: { // Brand Baseline (Identity)
    title: "The Brand Baseline",
    subtitle: "Your Identity",
    icon: Star,
    color: "gold",
    questions: [
      {
        id: "q8_athlete",
        type: "identity",
        standard: "Personal Branding",
        competency: "Self-Assessment",
        question: "You're building your personal brand. A sponsor asks: \"What makes you different from every other athlete?\" Which response shows the strongest Brand Identity?",
        options: [
          "I work harder than everyone else.",
          "I combine academic excellence with athletic performance - I'm a scholar-athlete who leads in the classroom and on the court.",
          "I have the most followers on social media.",
          "My coach says I'm the best player."
        ],
        correctAnswer: 1,
        coachHint: "Scholar-Athlete, remember your Three Pillars. A strong brand isn't just about athletic ability - it's about your complete identity. Which answer shows depth and differentiates you from the competition?"
      }
    ]
  }
};

// Parent (Executive Mirror) Questions
const PARENT_QUESTIONS = {
  week1: {
    title: "The Quantitative Combine",
    subtitle: "Executive Numbers",
    icon: Target,
    color: "orange",
    questions: [
      {
        id: "q8_parent",
        type: "math",
        standard: "ROI Calculation",
        competency: "Investment Analysis",
        question: "You are spending $200/mo on the Academy. If this helps your Scholar-Athlete secure a $5,000 academic scholarship, what is the Return on Investment (ROI) after 1 year of the program?",
        options: ["108%", "208%", "308%", "408%"],
        correctAnswer: 1,
        coachHint: "Scholar-Parent, we are looking at the yield on your investment. Calculate your total annual cost (12 months), then subtract that from the $5k gain. Divide that profit by your initial cost to find the ROI percentage."
      }
    ]
  },
  week2: {
    title: "Tactical Literacy",
    subtitle: "Executive Language",
    icon: BookOpen,
    color: "blue",
    questions: [
      {
        id: "q9_parent",
        type: "time",
        standard: "Time Management",
        competency: "Productivity Optimization",
        question: "You spend 10 hours a week \"coasting\" on low-priority tasks at work. If you reallocate 40% of that time to \"Deep Work\" on a promotion-track project, how many hours of high-impact work have you gained per month (4 weeks)?",
        options: ["12 hours", "14 hours", "16 hours", "18 hours"],
        correctAnswer: 2,
        coachHint: "Scholar-Parent, this is about your 'Professional Efficiency Rating.' Find 40% of those 10 hours first, then multiply that by 4 weeks to see the total time you've recovered for your career."
      }
    ]
  },
  week3: {
    title: "The Strategic Mind",
    subtitle: "Executive Strategy",
    icon: Brain,
    color: "purple",
    questions: [
      {
        id: "q10_parent",
        type: "identity",
        standard: "Brand Alignment",
        competency: "Professional Identity",
        question: "Your company's mission is \"Innovation.\" You have been using the same spreadsheet process for 5 years. Which action aligns with your professional \"Identity Audit\"?",
        options: [
          "Keep the spreadsheet - if it ain't broke, don't fix it",
          "Propose a new automation tool that increases efficiency by 40%",
          "Wait for your manager to suggest changes",
          "Complain about outdated processes in meetings"
        ],
        correctAnswer: 1,
        coachHint: "Scholar-Parent, if you were scouting your own professional 'film,' would you say your current process matches your company's 'Pillar of Innovation'? True leaders align their actions with their stated values."
      }
    ]
  },
  week4: {
    title: "The Brand Baseline",
    subtitle: "Executive Identity",
    icon: Star,
    color: "gold",
    questions: [
      {
        id: "q11_parent",
        type: "identity",
        standard: "Leadership Assessment",
        competency: "Executive Presence",
        question: "Your Scholar-Athlete asks you to review their brand statement. As a parent, what's the most effective coaching approach?",
        options: [
          "Rewrite it for them to make it perfect",
          "Ask guiding questions that help them refine their own voice",
          "Tell them it's fine and move on",
          "Compare it to other athletes' statements"
        ],
        correctAnswer: 1,
        coachHint: "Scholar-Parent, remember the Mirror effect. Your role is to guide, not dictate. Which approach develops their independence while still providing valuable coaching?"
      }
    ]
  }
};

const TheCombine = () => {
  const navigate = useNavigate();
  const [isParent, setIsParent] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showHuddle, setShowHuddle] = useState(false);
  const [huddleResponse, setHuddleResponse] = useState('');
  const [huddleLoading, setHuddleLoading] = useState(false);
  const [weekProgress, setWeekProgress] = useState({});
  const [powerScore, setPowerScore] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [coachConsultations, setCoachConsultations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [weekView, setWeekView] = useState('overview'); // 'overview' or 'quiz'

  const questions = isParent ? PARENT_QUESTIONS : ATHLETE_QUESTIONS;
  const weekKeys = ['week1', 'week2', 'week3', 'week4'];

  useEffect(() => {
    checkUserRole();
    loadProgress();
  }, []);

  const checkUserRole = () => {
    try {
      const userData = localStorage.getItem('academy_user');
      if (userData) {
        const user = JSON.parse(userData);
        setIsParent(user.is_parent === true);
      }
    } catch (e) {
      console.error('Error checking user role:', e);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await api.get('/academy/combine/progress');
      if (response.data.success) {
        const data = response.data.progress;
        setWeekProgress(data.week_progress || {});
        setPowerScore(data.academic_power_score || 0);
        setTotalAnswered(data.total_answered || 0);
        setCorrectAnswers(data.correct_answers || 0);
        setCoachConsultations(data.coach_consultations || 0);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const isWeekUnlocked = (weekNum) => {
    if (weekNum === 1) return true;
    const prevWeek = `week${weekNum - 1}`;
    return weekProgress[prevWeek]?.completed === true;
  };

  const getWeekStatus = (weekNum) => {
    const weekKey = `week${weekNum}`;
    if (weekProgress[weekKey]?.completed) return 'completed';
    if (isWeekUnlocked(weekNum)) return 'unlocked';
    return 'locked';
  };

  const startWeek = (weekNum) => {
    if (!isWeekUnlocked(weekNum)) {
      toast.error('Complete the previous week first!');
      return;
    }
    setCurrentWeek(weekNum);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setWeekView('quiz');
  };

  const getCurrentWeekData = () => {
    const weekKey = `week${currentWeek}`;
    return questions[weekKey];
  };

  const getCurrentQuestion = () => {
    const weekData = getCurrentWeekData();
    return weekData?.questions[currentQuestionIndex];
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) {
      toast.error('Please select an answer');
      return;
    }

    const question = getCurrentQuestion();
    const isCorrect = selectedAnswer === question.correctAnswer;
    setShowResult(true);

    try {
      await api.post('/academy/combine/answer', {
        question_id: question.id,
        week: currentWeek,
        selected_answer: selectedAnswer,
        is_correct: isCorrect,
        used_coach: showHuddle
      });

      // Update local state
      setTotalAnswered(prev => prev + 1);
      if (isCorrect) {
        setCorrectAnswers(prev => prev + 1);
        toast.success('Correct! Great play!');
      } else {
        toast.error('Not quite. Review the film and try again next time.');
      }

      // Recalculate power score
      const newCorrect = correctAnswers + (isCorrect ? 1 : 0);
      const newTotal = totalAnswered + 1;
      setPowerScore(Math.round((newCorrect / newTotal) * 100));

    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const handleNextQuestion = async () => {
    const weekData = getCurrentWeekData();
    
    if (currentQuestionIndex + 1 >= weekData.questions.length) {
      // Week complete
      try {
        await api.post('/academy/combine/complete-week', { week: currentWeek });
        setWeekProgress(prev => ({
          ...prev,
          [`week${currentWeek}`]: { completed: true }
        }));
        toast.success(`Week ${currentWeek} Complete! 🏆`);
        setWeekView('overview');
      } catch (error) {
        console.error('Error completing week:', error);
      }
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setShowHuddle(false);
      setHuddleResponse('');
    }
  };

  const callAsstCoach = async () => {
    if (huddleLoading) return;
    
    setHuddleLoading(true);
    setShowHuddle(true);

    try {
      const question = getCurrentQuestion();
      const response = await api.post('/academy/combine/asst-coach', {
        question_id: question.id,
        question_text: question.question,
        coach_hint: question.coachHint
      });

      if (response.data.success) {
        setHuddleResponse(response.data.coach_response);
        setCoachConsultations(prev => prev + 1);
      } else {
        setHuddleResponse(question.coachHint);
      }
    } catch (error) {
      console.error('Error calling Asst. Coach:', error);
      // Fallback to built-in hint
      setHuddleResponse(getCurrentQuestion().coachHint);
    } finally {
      setHuddleLoading(false);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-500/20 text-orange-500 border-orange-500/50',
      blue: 'bg-blue-500/20 text-blue-500 border-blue-500/50',
      purple: 'bg-purple-500/20 text-purple-500 border-purple-500/50',
      gold: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50'
    };
    return colors[color] || colors.orange;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Week Overview
  if (weekView === 'overview') {
    return (
      <div className="min-h-screen bg-black pt-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/5 border-b border-gray-800">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-orange-500" />
                </div>
                <div>
                  <h1 className="text-3xl font-black text-white">THE COMBINE</h1>
                  <p className="text-gray-400">
                    {isParent ? "Executive Mirror Edition" : "Scholar-Athlete Logic Engine"}
                  </p>
                </div>
              </div>
              
              {/* Power Score Display */}
              <div className="text-right">
                <div className="text-5xl font-black text-orange-500">{powerScore}</div>
                <div className="text-sm text-gray-400 font-bold">ACADEMIC POWER SCORE</div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-white">{totalAnswered}</div>
                <div className="text-xs text-gray-500">Questions Answered</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-500">{correctAnswers}</div>
                <div className="text-xs text-gray-500">Correct Answers</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-500">{coachConsultations}</div>
                <div className="text-xs text-gray-500">Coach Consultations</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-500">
                  {Object.values(weekProgress).filter(w => w?.completed).length}/4
                </div>
                <div className="text-xs text-gray-500">Weeks Complete</div>
              </CardContent>
            </Card>
          </div>

          {/* Week Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {weekKeys.map((weekKey, index) => {
              const weekNum = index + 1;
              const weekData = questions[weekKey];
              const status = getWeekStatus(weekNum);
              const IconComponent = weekData.icon;
              
              return (
                <Card 
                  key={weekKey}
                  className={`bg-gray-900 border-2 transition-all ${
                    status === 'locked' 
                      ? 'border-gray-800 opacity-60' 
                      : status === 'completed'
                        ? 'border-green-500/50'
                        : 'border-orange-500/50 hover:border-orange-500'
                  }`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(weekData.color)}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div>
                          <CardTitle className="text-white text-lg">Week {weekNum}</CardTitle>
                          <p className="text-sm text-gray-400">{weekData.title}</p>
                        </div>
                      </div>
                      {status === 'locked' && <Lock className="w-5 h-5 text-gray-600" />}
                      {status === 'completed' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-500 text-sm mb-4">{weekData.subtitle}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="border-gray-700 text-gray-400">
                        {weekData.questions.length} Questions
                      </Badge>
                      <Button
                        onClick={() => startWeek(weekNum)}
                        disabled={status === 'locked'}
                        className={`${
                          status === 'completed' 
                            ? 'bg-green-600 hover:bg-green-700' 
                            : 'bg-orange-500 hover:bg-orange-600'
                        } text-black font-bold`}
                        data-testid={`start-week-${weekNum}-btn`}
                      >
                        {status === 'completed' ? 'Review' : 'Start'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* 30-Day Sprint Enrollment CTA */}
          <Card 
            className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border-2 border-orange-500/30 hover:border-orange-500 transition-all cursor-pointer"
            onClick={() => navigate('/academy/grandmaster')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white">30-DAY SPRINT ENROLLMENT</h3>
                    <p className="text-gray-400 text-sm">Intensive Diagnostic • Performance Sprint • Three Pillars</p>
                  </div>
                </div>
                <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="sprint-enrollment-btn">
                  Start Sprint
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="mt-4 flex gap-2">
                <Badge variant="outline" className="border-blue-500/50 text-blue-400">Academic</Badge>
                <Badge variant="outline" className="border-green-500/50 text-green-400">Tactical</Badge>
                <Badge variant="outline" className="border-purple-500/50 text-purple-400">Brand</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quiz View
  const weekData = getCurrentWeekData();
  const question = getCurrentQuestion();
  const IconComponent = weekData?.icon || Target;

  return (
    <div className="min-h-screen bg-black pt-24">
      {/* Quiz Header */}
      <div className={`bg-gradient-to-r from-${weekData.color}-500/10 to-transparent border-b border-gray-800`}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => setWeekView('overview')}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(weekData.color)}`}>
                <IconComponent className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Week {currentWeek}: {weekData.title}</h2>
                <p className="text-sm text-gray-400">
                  Question {currentQuestionIndex + 1} of {weekData.questions.length}
                </p>
              </div>
            </div>
            
            {/* Power Score Mini */}
            <div className="text-right">
              <div className="text-3xl font-black text-orange-500">{powerScore}</div>
              <div className="text-xs text-gray-500">POWER SCORE</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <Progress 
            value={((currentQuestionIndex + 1) / weekData.questions.length) * 100} 
            className="mt-4 h-2 bg-gray-800"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Question Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Competency Tag */}
            <div className="flex items-center gap-2">
              <Badge className="bg-gray-800 text-gray-300 border-0">
                {question?.competency}
              </Badge>
              <Badge variant="outline" className="border-gray-700 text-gray-500 text-xs">
                {question?.standard}
              </Badge>
            </div>

            {/* Question Card */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <p className="text-white text-lg leading-relaxed mb-6">
                  {question?.question}
                </p>

                {/* Answer Options */}
                <div className="space-y-3">
                  {question?.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        showResult
                          ? index === question.correctAnswer
                            ? 'border-green-500 bg-green-500/10 text-green-400'
                            : index === selectedAnswer
                              ? 'border-red-500 bg-red-500/10 text-red-400'
                              : 'border-gray-700 text-gray-500'
                          : selectedAnswer === index
                            ? 'border-orange-500 bg-orange-500/10 text-white'
                            : 'border-gray-700 hover:border-gray-600 text-gray-300'
                      }`}
                      data-testid={`answer-option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          showResult
                            ? index === question.correctAnswer
                              ? 'bg-green-500 text-black'
                              : index === selectedAnswer
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-700 text-gray-400'
                            : selectedAnswer === index
                              ? 'bg-orange-500 text-black'
                              : 'bg-gray-700 text-gray-400'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-800">
                  {!showResult ? (
                    <>
                      <Button
                        onClick={callAsstCoach}
                        variant="outline"
                        disabled={huddleLoading}
                        className="flex-1 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        data-testid="asst-coach-btn"
                      >
                        {huddleLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Clipboard className="w-4 h-4 mr-2" />
                        )}
                        Asst. Coach
                      </Button>
                      <Button
                        onClick={handleSubmitAnswer}
                        disabled={selectedAnswer === null}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold"
                        data-testid="submit-answer-btn"
                      >
                        Lock In Answer
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleNextQuestion}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold"
                      data-testid="next-question-btn"
                    >
                      {currentQuestionIndex + 1 >= weekData.questions.length ? 'Complete Drill' : 'Next Question'}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - The Huddle */}
          <div className="space-y-4">
            <Card className={`bg-gray-900 border-2 transition-all ${showHuddle ? 'border-blue-500/50' : 'border-gray-800'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  The Huddle
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showHuddle ? (
                  <div className="space-y-3">
                    {huddleLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-blue-400 text-sm font-bold">
                          <Clipboard className="w-4 h-4" />
                          Asst. Coach Says:
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed italic">
                          "{huddleResponse}"
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Clipboard className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">
                      Need a hint? Click "Asst. Coach" to get guidance without the answer.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coachability Index */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Coachability Index</span>
                  <Badge variant="outline" className="border-blue-500/50 text-blue-400">
                    {coachConsultations} consultations
                  </Badge>
                </div>
                <p className="text-xs text-gray-600">
                  Athletes who seek coaching show higher growth rates.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheCombine;
